/**
 * @fileoverview Global Components Plugin for Vue.js
 * 
 * This module provides a Vue.js plugin that registers all commonly used components
 * as global components, making them available throughout the entire application
 * without the need for individual imports in each component file.
 * 
 * Includes components from:
 * - RhomTrip Assets library
 * - Element UI library
 * - Bootstrap Vue library
 * - Local application components
 * 
 * @module GlobalComponents
 * @requires vue
 * @author RhomTrip Development Team
 */

import BaseInput from "rhomtrip_assets/src/components/Inputs/BaseInput.vue";
import BaseDropdown from "rhomtrip_assets/src/components/BaseDropdown.vue";
import Card from "rhomtrip_assets/src/components/Cards/Card.vue";
import Modal from "rhomtrip_assets/src/components/Modal.vue";
import StatsCard from "rhomtrip_assets/src/components/Cards/StatsCard.vue";
import BaseButton from "rhomtrip_assets/src/components/BaseButton.vue";
import Badge from "rhomtrip_assets/src/components/Badge.vue";
import RouteBreadcrumb from "rhomtrip_assets/src/components/Breadcrumb/RouteBreadcrumb";
import BaseCheckbox from "rhomtrip_assets/src/components/Inputs/BaseCheckbox.vue";
import BaseSwitch from "rhomtrip_assets/src/components/BaseSwitch.vue";
import BaseRadio from "rhomtrip_assets/src/components/Inputs/BaseRadio";
import BaseProgress from "rhomtrip_assets/src/components/BaseProgress";
import CloseButton from "rhomtrip_assets/src/components/CloseButton";
import BasePagination from "rhomtrip_assets/src/components/BasePagination";
import BaseAlert from "rhomtrip_assets/src/components/BaseAlert";
import BaseNav from "rhomtrip_assets/src/components/Navbar/BaseNav";
import NavbarToggleButton from "rhomtrip_assets/src/components/Navbar/NavbarToggleButton.vue";
import BaseHeader from "rhomtrip_assets/src/components/BaseHeader";
import {
  Tooltip,
  Popover,
  DatePicker,
  Select,
  Option,
  OptionGroup,
  FormItem,
  Form
} from "element-ui";
import Tabs from "rhomtrip_assets/src/components/Tabs/Tabs.vue";
import Tab from "rhomtrip_assets/src/components/Tabs/Tab.vue";
import ButtonRadioGroup from "rhomtrip_assets/src/components/ButtonRadioGroup.vue";
import {
  BootstrapVue,
  BootstrapVueIcons,
  DropdownPlugin,
  TablePlugin
} from "bootstrap-vue";
import PaginationBlock from "../components/PaginationBlock.vue";
import VueTextareaAutosize from "vue-textarea-autosize";
import Input from "rhomtrip_assets/src/components/Inputs/el-input";
import sidebarhelp from "rhomtrip_assets/src/components/SidebarPlugin/SideBarHelp";
import sidebarRelease from "rhomtrip_assets/src/components/SidebarPlugin/SideBarReleases";
import Loader from "rhomtrip_assets/src/components/Loader/Loader";
import ButtonCheckbox from "rhomtrip_assets/src/components/ButtonCheckbox";
import ButtonRadio from "rhomtrip_assets/src/components/ButtonRadioGroup";
import collapse from "rhomtrip_assets/src/components/Collapse/Collapse";
import collapseItem from "rhomtrip_assets/src/components/Collapse/CollapseItem";
import LazyImage from "rhomtrip_assets/src/components/LazyImage/LazyImage.vue";
import ActionLink from "../components/ActionLink.vue";
import DropdownActionItem from "../components/DropdownActionItem.vue";
import BaseNumberInput from "../components/Inputs/BaseNumberInput.vue";

/**
 * Global Components Plugin Object
 * 
 * A Vue.js plugin that registers commonly used components globally across the application.
 * This eliminates the need to import components individually in each Vue component file.
 * 
 * @namespace GlobalComponents
 * @type {Object}
 * @property {Function} install - The plugin installation function called by Vue.use()
 */
const GlobalComponents = {
  /**
   * Plugin installation function
   * 
   * This function is automatically called when the plugin is installed via Vue.use().
   * It registers all imported components as global components and installs necessary plugins.
   * 
   * @param {Object} Vue - The Vue constructor/instance
   * @returns {void}
   * 
   * @description
   * Registers the following component categories:
   * - Base UI components (buttons, inputs, cards, etc.)
   * - Navigation components (breadcrumbs, navbar, pagination)
   * - Form components (inputs, checkboxes, radios, selects)
   * - Layout components (modals, collapse, tabs)
   * - Utility components (loader, badges, alerts)
   * - Third-party plugin integrations (Element UI, Bootstrap Vue)
   * 
   * @example
   * // Usage in main.js
   * import GlobalComponents from './plugins/globalComponents'
   * Vue.use(GlobalComponents)
   * 
   * // Now components can be used globally without imports:
   * // <BaseButton>Click me</BaseButton>
   * // <Card>Content</Card>
   */
  install(Vue) {
    // Register Base UI Components
    Vue.component(Badge.name, Badge);
    Vue.component(BaseAlert.name, BaseAlert);
    Vue.component(BaseButton.name, BaseButton);
    Vue.component(BaseCheckbox.name, BaseCheckbox);
    Vue.component(BaseHeader.name, BaseHeader);
    Vue.component(BaseInput.name, BaseInput);
    Vue.component(BaseDropdown.name, BaseDropdown);
    Vue.component(BaseNav.name, BaseNav);
    Vue.component(NavbarToggleButton.name, NavbarToggleButton);
    Vue.component(BasePagination.name, BasePagination);
    Vue.component(BaseProgress.name, BaseProgress);
    Vue.component(CloseButton.name, CloseButton);
    Vue.component(BaseRadio.name, BaseRadio);
    Vue.component(BaseSwitch.name, BaseSwitch);

    // Register Card Components
    Vue.component(Card.name, Card);
    Vue.component(Modal.name, Modal);
    Vue.component(StatsCard.name, StatsCard);

    // Register Navigation Components
    Vue.component(RouteBreadcrumb.name, RouteBreadcrumb);
    Vue.component(Input.name, Input);

    // Register Element UI Components and Plugins
    Vue.use(Tooltip);
    Vue.use(Popover);
    Vue.use(DatePicker);
    Vue.component(Select.name, Select);
    Vue.component(Option.name, Option);
    Vue.use(Select);
    Vue.use(Option);
    Vue.use(OptionGroup);

    // Register Tab Components
    Vue.component(Tabs.name, Tabs);
    Vue.component(Tab.name, Tab);
    Vue.component(ButtonRadioGroup.name, ButtonRadioGroup);

    /**
     * Custom Select Component Enhancement
     * 
     * Modifies the Element UI Select component's readonly computed property
     * to handle Internet Explorer compatibility and improve behavior.
     * 
     * @memberof GlobalComponents.install
     * @description
     * Overrides the default readonly behavior to:
     * - Detect Internet Explorer browser
     * - Handle filterable, multiple, visible, and remote properties
     * - Ensure proper readonly state based on component configuration
     */
    Select.computed.readonly = function () {
      const isIE =
        !this.$isServer && !Number.isNaN(Number(document.documentMode));
      return (
        !this.filterable ||
        this.multiple ||
        (!isIE && !this.visible && !this.remote)
      );
    };

    // Register Bootstrap Vue Plugins
    Vue.use(BootstrapVue);
    Vue.use(BootstrapVueIcons);
    Vue.use(DropdownPlugin);
    Vue.use(TablePlugin);

    // Register Custom Application Components
    Vue.component(PaginationBlock.name, PaginationBlock);

    // Register Text Area Plugin
    Vue.use(VueTextareaAutosize);

    // Register Sidebar Components
    Vue.component(sidebarhelp.name, sidebarhelp);
    Vue.component(sidebarRelease.name, sidebarRelease);

    // Register Utility Components
    Vue.component(Loader.name, Loader);
    Vue.component(ButtonCheckbox.name, ButtonCheckbox);
    Vue.component(ButtonRadio.name, ButtonRadio);

    // Register Layout Components
    Vue.component(collapse.name, collapse);
    Vue.component(collapseItem.name, collapseItem);

    // Register Form Components
    Vue.use(Form);
    Vue.use(FormItem);

    // Register Media and Action Components
    Vue.component(LazyImage.name, LazyImage);
    Vue.component(ActionLink.name, ActionLink);
    Vue.component(DropdownActionItem.name, DropdownActionItem);

    // Register Specialized Input Components
    Vue.component(BaseNumberInput.name, BaseNumberInput);
  }
};

/**
 * Export the Global Components Plugin
 * 
 * @exports GlobalComponents
 * @default GlobalComponents
 * @description
 * Default export of the GlobalComponents plugin object.
 * This can be imported and used with Vue.use() to register all components globally.
 * 
 * @example
 * import GlobalComponents from '@/plugins/globalComponents'
 * Vue.use(GlobalComponents)
 */
export default GlobalComponents;
